import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { IMG, SERVICES, USPS, PROCESS, CLIENTS } from "../data/content";
import { Reveal } from "../lib/motion";
import { Btn } from "../components/Btn";
import SectionHeading from "../components/SectionHeading";
import ContactCTA from "../components/ContactCTA";

const HERO_LINES = ["Projectmanagement", "met overzicht,", "vertrouwen", "& resultaat."];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[92vh] flex items-center pt-28 pb-16 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-ballk-purple/[0.06] blur-3xl" />
      </div>
      <div className="container-ballk grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <div className="lg:col-span-6">
          <h1 data-testid="hero-title" className="font-heading font-bold text-ballk-indigo text-[2.75rem] sm:text-6xl lg:text-[3.75rem] leading-[1.02] tracking-tight">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="text-mask-line pb-1">
                <motion.span
                  className="block will-change-transform"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 2 ? <span className="text-ballk-purple">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-7 text-ballk-steel text-base md:text-lg max-w-lg leading-relaxed">
            BALLK Project begeleidt renovatie-, onderhouds- en werktuigbouwkundige projecten voor uiteenlopende vastgoedtypen waaronder woningen, bedrijfspanden en zorgvastgoed.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap gap-4">
            <Btn to="/contact" data-testid="hero-cta-contact">Neem contact op <ArrowUpRight size={18} weight="bold" /></Btn>
            <Btn to="/diensten" variant="secondary" data-testid="hero-cta-diensten">Bekijk diensten</Btn>
          </motion.div>
        </div>

        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.3 }}
            className="relative">
            <div className="relative overflow-hidden rounded-[20px] diag-img shadow-[0_30px_80px_-30px_rgba(26,31,54,0.35)]">
              <motion.img
                src={IMG.hero} alt="Modern zorggebouw" style={{ y, scale }}
                className="w-full h-[420px] lg:h-[560px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-ballk-light shadow-[0_20px_50px_-20px_rgba(26,31,54,0.25)] px-6 py-5">
              <p className="font-heading font-bold text-3xl text-ballk-purple">15+</p>
              <p className="text-xs text-ballk-steel mt-1">jaar ervaring<br />in vastgoed</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ValuesStrip() {
  const items = Array(6).fill("Samen bouwen aan wat belangrijk is.");
  return (
    <section data-testid="values-marquee" className="border-y border-ballk-light py-6 bg-white">
      <Marquee speed={40} gradient gradientColor="#ffffff" gradientWidth={80}>
        {items.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-5 font-heading font-semibold text-2xl md:text-3xl text-black">
            <span className="text-ballk-purple text-xl">◆</span>
            {v}
            <span className="text-ballk-purple text-xl">◆</span>
            <span aria-hidden className="inline-block w-40 md:w-64" />
          </span>
        ))}
      </Marquee>
    </section>
  );
}

function Over() {
  return (
    <section data-testid="over-section" className="py-24 md:py-32">
      <div className="container-ballk grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <Reveal>
          <div className="relative">
            <div className="overflow-hidden rounded-[20px]" style={{ clipPath: "polygon(4% 0, 100% 0, 96% 100%, 0 100%)" }}>
              <img src={IMG.meeting} alt="Projectoverleg" className="w-full h-[460px] object-cover" />
            </div>
          </div>
        </Reveal>
        <div>
          <SectionHeading eyebrow="Over BALLK" title="Persoonlijk, technisch onderlegd en altijd in controle."
            intro="BALLK Project is uw onafhankelijke partner voor complexe bouw- en installatieopgaven. Wij combineren technische expertise met een persoonlijke aanpak en korte lijnen." />
          <div className="mt-8 space-y-4">
            {["Persoonlijke aanpak en korte lijnen", "Diepgaande technische expertise", "Volledige projectbeheersing", "Onafhankelijk advies"].map((p) => (
              <Reveal key={p}>
                <div className="flex items-start gap-3">
                  <CheckCircle size={22} weight="fill" className="text-ballk-purple shrink-0 mt-0.5" />
                  <span className="text-ballk-indigo font-medium">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Btn to="/over-ons" variant="secondary" className="mt-9">Meer over ons <ArrowRight size={16} weight="bold" /></Btn>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Diensten() {
  return (
    <section data-testid="diensten-section" className="py-24 md:py-32 bg-[#FAFAFB] border-y border-ballk-light">
      <div className="container-ballk">
        <SectionHeading eyebrow="Diensten" title="Van regie tot techniek — onder één dak."
          intro="Complete begeleiding van uw project, met scherpe aandacht voor kwaliteit, planning en budget." />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.05}>
              <Link to="/diensten" data-testid={`service-card-${s.slug}`}
                className="group block h-full bg-white rounded-2xl p-8 border border-ballk-light shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:-translate-y-2 hover:shadow-[0_18px_50px_-12px_rgba(26,31,54,0.15)] transition-all duration-500">
                <div className="h-14 w-14 rounded-xl bg-ballk-purple/10 flex items-center justify-center text-ballk-purple group-hover:bg-ballk-purple group-hover:text-white transition-colors duration-300">
                  <s.icon size={28} weight="duotone" />
                </div>
                <h3 className="mt-6 font-heading font-semibold text-xl text-ballk-indigo">{s.title}</h3>
                <p className="mt-3 text-sm text-ballk-steel leading-relaxed">{s.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-ballk-purple opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-300">
                  Meer info <ArrowRight size={15} weight="bold" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Waarom() {
  return (
    <section data-testid="waarom-section" className="py-24 md:py-32">
      <div className="container-ballk">
        <SectionHeading eyebrow="Waarom BALLK" title="Vier redenen om samen te werken." />
        <div className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {USPS.map((u, i) => (
            <Reveal key={u.title} delay={i * 0.08}>
              <div className="border-t border-ballk-light pt-6">
                <span className="font-heading font-bold text-5xl text-ballk-light">0{i + 1}</span>
                <div className="mt-5 text-ballk-purple"><u.icon size={30} weight="duotone" /></div>
                <h3 className="mt-4 font-heading font-semibold text-lg text-ballk-indigo">{u.title}</h3>
                <p className="mt-2.5 text-sm text-ballk-steel leading-relaxed">{u.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Werkwijze() {
  return (
    <section data-testid="werkwijze-section" className="py-24 md:py-32">
      <div className="container-ballk">
        <SectionHeading eyebrow="Werkwijze" title="Een helder pad van kennismaking tot oplevering."
          intro="Vijf overzichtelijke fases, met u als opdrachtgever altijd in de lead." />
        <div className="mt-16 relative">
          <div className="hidden lg:block absolute top-7 left-0 right-0 h-px bg-ballk-light" />
          <div className="grid gap-10 lg:grid-cols-5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.1}>
                <div className="relative">
                  <div className="h-14 w-14 rounded-full bg-white border-2 border-ballk-purple flex items-center justify-center text-ballk-purple relative z-10">
                    <p.icon size={26} weight="duotone" />
                  </div>
                  <span className="mt-5 block font-heading font-bold text-sm text-ballk-purple">{p.step}</span>
                  <h3 className="mt-1 font-heading font-semibold text-lg text-ballk-indigo">{p.title}</h3>
                  <p className="mt-2 text-sm text-ballk-steel leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Klanten() {
  return (
    <section data-testid="klanten-section" className="py-20 bg-[#FAFAFB] border-y border-ballk-light">
      <div className="container-ballk">
        <p className="text-center text-xs uppercase tracking-[0.25em] text-ballk-steel mb-10">Vertrouwd door opdrachtgevers</p>
      </div>
      <Marquee speed={35} gradient gradientColor="#FAFAFB" gradientWidth={100} pauseOnHover>
        {[...CLIENTS, ...CLIENTS].map((c, i) => (
          <span key={i} className="mx-10 font-heading font-semibold text-xl text-ballk-steel/60 hover:text-ballk-purple transition-colors">
            {c}
          </span>
        ))}
      </Marquee>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <ValuesStrip />
      <Over />
      <Diensten />
      <Waarom />
      <Werkwijze />
      <ContactCTA />
    </>
  );
}
